import React, { useCallback, useId, useState } from "react"

import { MetricModal } from "@/metrics/configure"
import { useMetricRecords } from "@/metrics/registry"
import { useGetOrgRecordTransport } from "@/organization"
import { rerunMetricsV1 } from "@/runs"
import { useWorkspaceContext } from "@/workspaces"
import { Button, toast } from "@coval/ui"
import { RotateCw } from "lucide-react"

import { LocalWorkerRoutingWarning } from "@/components/LocalWorkerRoutingWarning"
import { useDevId } from "@/hooks/useDevId"

import type { RerunMetricsById } from "../types"

const ORGANIZATION_QUERY_STALE_TIME_MS = 5 * 60 * 1000
const MAX_TOTAL_RERUNS = 500

interface AddMetricsButtonProps {
  useLimit?: boolean
  metricRecordsById?: RerunMetricsById
  simulationOutputIds?: string[]
  variant?: "solid" | "flat"
  selectedSimulationOutputIds?: string[]
  onSuccess?: () => void
  /**
   * When provided, the button skips the metric-picker modal and launches a
   * rerun for exactly these metric ids. Used by the Reports drill-in view
   * where the metric is already fixed.
   */
  forcedMetricIds?: string[]
  buttonLabelOverride?: string
  disabled?: boolean
  title?: string
}

export const AddMetricsToRunButton: React.FC<AddMetricsButtonProps> = ({
  metricRecordsById,
  simulationOutputIds,
  selectedSimulationOutputIds = [],
  onSuccess,
  forcedMetricIds,
  buttonLabelOverride,
  disabled = false,
  title,
}) => {
  const instanceId = useId()
  const devId = useDevId()
  const { currentWorkspace } = useWorkspaceContext()
  const { metricRecords } = useMetricRecords()
  const availableMetricRecords = metricRecordsById ?? metricRecords

  const [isOpen, setIsOpen] = useState(false)
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([])

  const isForcedSingleLaunch = (forcedMetricIds?.length ?? 0) > 0

  const {
    data: organization,
    isError: isOrganizationError,
    isLoading: isOrganizationLoading,
  } = useGetOrgRecordTransport(true, {
    staleTime: ORGANIZATION_QUERY_STALE_TIME_MS,
  })

  const organizationId = organization?.id

  const handleRunMetrics = async (metricIdsOverride?: string[]) => {
    const metricIds = metricIdsOverride ?? selectedMetrics
    if (metricIds.length === 0) {
      return
    }

    const resolvedMetricIds = metricIds.map(
      (metricId) =>
        availableMetricRecords?.[metricId]?.id ??
        Object.values(availableMetricRecords ?? {}).find(
          (metric) => metric.metric_name === metricId,
        )?.id ??
        metricId,
    )

    setIsOpen(false)

    toast.info({
      title: "Re-running metrics...",
      description: "This may take a few minutes",
    })

    try {
      let simulation_output_ids: string[] = []

      if (selectedSimulationOutputIds.length > 0) {
        simulation_output_ids = selectedSimulationOutputIds
      } else if (simulationOutputIds) {
        for (const id of simulationOutputIds) {
          simulation_output_ids.push(id)
        }
      } else {
        simulation_output_ids = []
      }

      const originalLength = simulation_output_ids.length

      simulation_output_ids = [
        ...new Set(
          simulation_output_ids.filter(
            (id) => typeof id === "string" && id.trim() !== "",
          ),
        ),
      ]

      if (simulation_output_ids.length !== originalLength) {
        const filtered = originalLength - simulation_output_ids.length
        toast.warning({
          title: "Warning",
          description: `Filtered out ${filtered} invalid/duplicate simulation_output_ids`,
        })
      }

      if (simulation_output_ids.length === 0) {
        toast.danger({
          title: "Unable to re-run metrics",
          description: "No conversations were found to evaluate.",
        })
        return
      }

      if (
        simulation_output_ids.length * resolvedMetricIds.length >
        MAX_TOTAL_RERUNS
      ) {
        toast.danger({
          title: "Too many reruns selected",
          description: `Please select at most ${MAX_TOTAL_RERUNS} simulation and metric combinations.`,
        })
        return
      }

      if (!organizationId) {
        toast.danger({
          title: "Organization settings unavailable",
          description: "Please wait a moment and try again.",
        })
        return
      }

      const result = await rerunMetricsV1({
        simulation_ids: simulation_output_ids,
        metric_ids: resolvedMetricIds,
        ...(devId ? { dev_id: devId } : {}),
        workspace_id: currentWorkspace?.id ?? null,
      })

      if (
        typeof result === "object" &&
        result !== null &&
        "failed_batch_count" in result &&
        typeof result.failed_batch_count === "number" &&
        "successful_batch_count" in result &&
        typeof result.successful_batch_count === "number"
      ) {
        toast.warning({
          title: "Some reruns need attention",
          description: `${result.failed_batch_count} batch(es) failed after ${result.successful_batch_count} batch(es) were queued.`,
        })
        return
      }

      toast.success({
        title: "Success",
        description: devId
          ? `Queued for the worker matching ${devId}`
          : "Metrics re-run successfully",
      })

      onSuccess?.()

      setTimeout(() => {
        setIsOpen(false)
      }, 1500)
    } catch (error) {
      toast.error({
        error,
        fallback: "Failed to re-run metrics. Please try again.",
        title: "Unable to re-run metrics",
      })
    }
  }

  const handleOpenModal = () => {
    if (isOrganizationError || isOrganizationLoading || !organizationId) {
      toast.danger({
        title: "Organization settings unavailable",
        description: "Please wait a moment and try again.",
      })
      return
    }
    if (isForcedSingleLaunch) {
      void handleRunMetrics(forcedMetricIds)
      return
    }
    setSelectedMetrics([])
    setIsOpen(true)
  }

  const handleCloseModal = useCallback(() => {
    setSelectedMetrics([])
    setIsOpen(false)
  }, [])

  const handleToggleMetric = useCallback(
    (metric_id: string, isSelected: boolean) => {
      setSelectedMetrics((prevMetrics) => {
        if (isSelected) {
          return [...prevMetrics, metric_id]
        }
        return prevMetrics.filter((m) => m !== metric_id)
      })
    },
    [],
  )

  const hasSelection = selectedSimulationOutputIds.length > 0
  const defaultIdleLabel = isForcedSingleLaunch
    ? "Rerun Metric"
    : "Rerun Metrics"
  const buttonLabel =
    buttonLabelOverride ??
    (hasSelection
      ? `Rerun (${selectedSimulationOutputIds.length})`
      : defaultIdleLabel)
  const buttonTitle = hasSelection
    ? `${defaultIdleLabel} on ${selectedSimulationOutputIds.length} selected row${selectedSimulationOutputIds.length === 1 ? "" : "s"}`
    : `${defaultIdleLabel} on all rows`

  const isDisabled =
    disabled || isOrganizationLoading || isOrganizationError || !organizationId

  return (
    <>
      <Button
        variant="secondary"
        onClick={handleOpenModal}
        title={title ?? buttonTitle}
        disabled={isDisabled}
      >
        <RotateCw />
        {buttonLabel}
      </Button>

      {isOpen && !isForcedSingleLaunch && (
        <MetricModal
          isOpen={isOpen}
          banner={<LocalWorkerRoutingWarning className="mb-4" />}
          onClose={handleCloseModal}
          onApply={() => {
            void handleRunMetrics()
          }}
          metrics={Object.values(metricRecordsById || {})}
          selectedMetrics={selectedMetrics}
          onToggleMetric={handleToggleMetric}
          instanceId={instanceId}
          allowMultipleSelection
        />
      )}
    </>
  )
}
